import { useEffect, useMemo, useRef, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Flag,
  Target,
  ChevronRight,
  ChevronDown,
  Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useAppSelector } from "@/stores/hook";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import { usePermissions } from "@/rbac/usePermissions";
import { useStatusRbac } from "@/rbac/useStatusRbac";
import { toast } from "sonner";

const DEFAULT_NUDGE = "2";

type StatusOption = {
  id: number;
  stageId: number;
  stageName: string;
  statusName: string;
  subStatuses: { id: number; name: string }[];
};

function flattenPipeline(pipeline: any[] | undefined): StatusOption[] {
  if (!pipeline?.length) return [];
  const out: StatusOption[] = [];
  for (const stage of pipeline) {
    for (const s of stage.statuses ?? []) {
      out.push({
        id: s.id,
        stageId: stage.id,
        stageName: stage.name,
        statusName: s.name,
        subStatuses: (s.subStatuses ?? []).map((sub: any) => ({
          id: sub.id,
          name: sub.name,
        })),
      });
    }
  }
  return out;
}

interface DeadlineSetterPopoverProps {
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
}

export default function DeadlineSetterPopover({
  children,
  align = "end",
  side = "top",
}: DeadlineSetterPopoverProps) {
  const [open, setOpen] = useState(false);
  const { addDeadline, activeData } = useActiveDataMutation();
  const {
    currentUser: { user },
  } = useAppSelector((state) => state.auth);
  const { pipeline } = useLeadPipeline();
  const { canDeadline } = usePermissions();
  const currentStageIdNum = useMemo(() => {
    const raw = activeData?.stage?.id ?? activeData?.stageId;
    if (raw == null || raw === "") return NaN;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  }, [activeData]);
  const canSetDeadline = canDeadline(currentStageIdNum, "edit");

  const statusOptions = useMemo(() => flattenPipeline(pipeline), [pipeline]);

  const stageItems = useMemo(() => {
    if (!pipeline?.length) return [] as { id: string; name: string }[];
    return pipeline.map((s: any) => ({ id: String(s.id), name: s.name }));
  }, [pipeline]);

  // From defaults to record's current status
  const currentStatusId =
    activeData?.statusId ?? activeData?.status?.id ?? null;
  const currentStatusMeta = useMemo(() => {
    if (currentStatusId == null) return null;
    return (
      statusOptions.find((s) => String(s.id) === String(currentStatusId)) ??
      null
    );
  }, [statusOptions, currentStatusId]);

  const [showFromEditor, setShowFromEditor] = useState(false);
  const [fromStageId, setFromStageId] = useState<string>("");
  const [fromStatusId, setFromStatusId] = useState<string>("");
  const [fromSubId, setFromSubId] = useState<string>("");

  const [toStageId, setToStageId] = useState<string>("");
  const [toStatusId, setToStatusId] = useState<string>("");
  const [toSubId, setToSubId] = useState<string>("");

  const [nudge, setNudge] = useState(DEFAULT_NUDGE);
  const [note, setNote] = useState("");

  // Reset on open / when entity changes
  const seedRef = useRef<{ entityId?: number | string; seeded: boolean }>({
    seeded: false,
  });
  useEffect(() => {
    if (!open) return;
    if (
      seedRef.current.entityId === activeData?.id &&
      seedRef.current.seeded
    ) {
      return;
    }
    if (currentStatusMeta) {
      setFromStageId(String(currentStatusMeta.stageId));
      setFromStatusId(String(currentStatusMeta.id));
    } else {
      setFromStageId("");
      setFromStatusId("");
    }
    setFromSubId("");
    setToStageId("");
    setToStatusId("");
    setToSubId("");
    setNudge(DEFAULT_NUDGE);
    setNote("");
    setShowFromEditor(false);
    seedRef.current = { entityId: activeData?.id, seeded: true };
  }, [open, activeData?.id, currentStatusMeta]);

  // Also reset seed when popover closes so reopening with a new entity reseeds
  useEffect(() => {
    if (!open) seedRef.current.seeded = false;
  }, [open]);

  // Role-denied statuses (wizard "Status" block) are not offered as
  // milestone anchors. The entity's CURRENT status stays pickable as the
  // natural "from" anchor even when denied; in-flight picks are pinned so
  // the trigger never blanks mid-edit. `fromMeta`/`toMeta` below resolve
  // against the UNFILTERED list — existing values must keep their labels.
  const { filterRoleAllowedStatuses } = useStatusRbac();
  const fromStatuses = useMemo(
    () =>
      filterRoleAllowedStatuses(
        fromStageId,
        statusOptions.filter((s) => String(s.stageId) === fromStageId),
        [currentStatusId, fromStatusId]
      ),
    [
      statusOptions,
      fromStageId,
      filterRoleAllowedStatuses,
      currentStatusId,
      fromStatusId,
    ]
  );
  const toStatuses = useMemo(
    () =>
      filterRoleAllowedStatuses(
        toStageId,
        statusOptions.filter((s) => String(s.stageId) === toStageId),
        [toStatusId]
      ),
    [statusOptions, toStageId, filterRoleAllowedStatuses, toStatusId]
  );
  const fromMeta = statusOptions.find((s) => String(s.id) === fromStatusId);
  const toMeta = statusOptions.find((s) => String(s.id) === toStatusId);

  const onFromStageChange = (id: string) => {
    setFromStageId(id);
    setFromStatusId("");
    setFromSubId("");
  };
  const onToStageChange = (id: string) => {
    setToStageId(id);
    setToStatusId("");
    setToSubId("");
  };
  const onFromStatusChange = (id: string) => {
    setFromStatusId(id);
    setFromSubId("");
  };
  const onToStatusChange = (id: string) => {
    setToStatusId(id);
    setToSubId("");
  };

  const handleAdd = () => {
    if (!user) return;
    if (!fromStatusId) {
      toast.error("Pick a from-status");
      return;
    }
    if (!toStatusId) {
      toast.error("Pick a target status");
      return;
    }
    if (fromStatusId === toStatusId && !fromSubId && !toSubId) {
      toast.error(
        "From and target must differ unless sub-statuses refine the move"
      );
      return;
    }
    if (
      fromStatusId === toStatusId &&
      fromSubId &&
      toSubId &&
      fromSubId === toSubId
    ) {
      toast.error("Select a different target sub-status");
      return;
    }
    const wd = Number(nudge);
    if (!Number.isFinite(wd) || wd <= 0) {
      toast.error("Enter a valid number of days");
      return;
    }
    const warningDays = Math.min(3650, Math.floor(wd));

    const payload: Record<string, unknown> = {
      fromStatusId: Number(fromStatusId),
      toStatusId: Number(toStatusId),
      createdBy: { id: user.id, name: user.name },
      createdAt: new Date().toISOString(),
      status: "Pending",
      completedBy: {},
      warningDays,
    };
    if (fromMeta) {
      payload.fromStatus = {
        id: fromMeta.id,
        name: fromMeta.statusName,
        stageId: fromMeta.stageId,
      };
    }
    if (toMeta) {
      payload.toStatus = {
        id: toMeta.id,
        name: toMeta.statusName,
        stageId: toMeta.stageId,
      };
    }
    if (fromSubId) payload.fromSubStatusId = Number(fromSubId);
    if (toSubId) payload.toSubStatusId = Number(toSubId);
    if (note.trim()) {
      payload.note = {
        note: note.trim(),
        createdById: user.id,
        createdByName: user.name,
        createdAt: new Date().toISOString(),
      };
    }

    addDeadline(payload);
    toast.success("Milestone set — completes on target status");
    setOpen(false);
  };

  const canSubmit = useMemo(() => {
    if (!fromStatusId || !toStatusId) return false;
    if (fromStatusId === toStatusId && !fromSubId && !toSubId) return false;
    if (
      fromStatusId === toStatusId &&
      fromSubId &&
      toSubId &&
      fromSubId === toSubId
    )
      return false;
    const wd = Number(nudge);
    if (!Number.isFinite(wd) || wd <= 0) return false;
    return true;
  }, [fromStatusId, toStatusId, fromSubId, toSubId, nudge]);

  if (!canSetDeadline) {
    return <>{children}</>;
  }

  const fromChipLabel = fromMeta
    ? [fromMeta.statusName, fromMeta.subStatuses.find((s) => String(s.id) === fromSubId)?.name]
        .filter(Boolean)
        .join(" · ")
    : "Choose from";

  const SUBTLE_TRIGGER =
    "h-9 w-full text-[12.5px] rounded-[10px] border-0 bg-[var(--color-black-2)]/40 hover:bg-[var(--color-black-2)]/60 shadow-none ring-0 focus:ring-2 focus:ring-[var(--color-success)]/15 data-[placeholder]:text-[var(--color-text-gray)] px-3";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={8}
        className="w-[380px] p-0 rounded-[20px] border-[var(--color-black-2)] shadow-[0px_8px_40px_-4px_rgba(0,0,0,0.18)] bg-white overflow-hidden"
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-[var(--color-black-1)]/60">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-[10px] bg-[var(--color-success)]/10">
              <Flag className="size-3.5 text-[var(--color-success)]" strokeWidth={2.25} />
            </div>
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-[var(--color-text-primary)] leading-tight">
                Set milestone
              </p>
              <p className="text-[10.5px] text-[var(--color-text-gray)] mt-0.5 leading-tight">
                Auto-completes when target status is reached
              </p>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 space-y-3 max-h-[440px] overflow-y-auto">
          {/* From row (compact, current status pre-filled) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-gray)]">
                From
              </span>
              <button
                type="button"
                onClick={() => setShowFromEditor((v) => !v)}
                className="inline-flex items-center gap-1 text-[10.5px] font-medium text-[var(--color-success)] hover:text-[var(--color-success)]/80 transition-colors"
              >
                <Pencil className="size-3" />
                {showFromEditor ? "Done" : "Change"}
              </button>
            </div>

            {!showFromEditor ? (
              <div className="flex items-center gap-2 h-9 px-3 rounded-[10px] bg-[var(--color-black-2)]/30 border border-[var(--color-black-1)]/50">
                <span
                  className="size-1.5 rounded-full bg-[var(--color-success)]"
                  aria-hidden
                />
                <span className="text-[12.5px] font-medium text-[var(--color-text-primary)] truncate">
                  {fromChipLabel}
                </span>
                {fromMeta?.stageName ? (
                  <span className="text-[10.5px] text-[var(--color-text-gray)] truncate ml-auto">
                    {fromMeta.stageName}
                  </span>
                ) : null}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Select value={fromStageId} onValueChange={onFromStageChange}>
                  <SelectTrigger className={SUBTLE_TRIGGER}>
                    <SelectValue placeholder="Stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stageItems.map((s: { id: string; name: string }) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={fromStatusId}
                  onValueChange={onFromStatusChange}
                  disabled={!fromStageId}
                >
                  <SelectTrigger className={SUBTLE_TRIGGER}>
                    <SelectValue
                      placeholder={fromStageId ? "Status" : "Pick stage"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {fromStatuses.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.statusName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fromMeta && fromMeta.subStatuses.length > 0 ? (
                  <div className="col-span-2">
                    <Select
                      value={fromSubId || "__none__"}
                      onValueChange={(v) =>
                        setFromSubId(v === "__none__" ? "" : v)
                      }
                    >
                      <SelectTrigger className={SUBTLE_TRIGGER}>
                        <SelectValue placeholder="Sub-status (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          Any — main status
                        </SelectItem>
                        {fromMeta.subStatuses.map((sub) => (
                          <SelectItem key={sub.id} value={String(sub.id)}>
                            {sub.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Arrow divider */}
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-1.5 text-[var(--color-text-gray)]">
              <span className="h-px w-12 bg-[var(--color-black-1)]" />
              <ChevronDown className="size-3.5" />
              <span className="h-px w-12 bg-[var(--color-black-1)]" />
            </div>
          </div>

          {/* Target row */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-gray)]">
                Target
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] text-[var(--color-text-gray)]">
                <Target className="size-3" />
                where you want it
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={toStageId} onValueChange={onToStageChange}>
                <SelectTrigger className={SUBTLE_TRIGGER}>
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  {stageItems.map((s: { id: string; name: string }) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={toStatusId}
                onValueChange={onToStatusChange}
                disabled={!toStageId}
              >
                <SelectTrigger className={SUBTLE_TRIGGER}>
                  <SelectValue
                    placeholder={toStageId ? "Status" : "Pick stage"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {toStatuses.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.statusName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {toMeta && toMeta.subStatuses.length > 0 ? (
                <div className="col-span-2">
                  <Select
                    value={toSubId || "__none__"}
                    onValueChange={(v) =>
                      setToSubId(v === "__none__" ? "" : v)
                    }
                  >
                    <SelectTrigger className={SUBTLE_TRIGGER}>
                      <SelectValue placeholder="Sub-status (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        Any — main status
                      </SelectItem>
                      {toMeta.subStatuses.map((sub) => (
                        <SelectItem key={sub.id} value={String(sub.id)}>
                          {sub.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          </div>

          {/* Nudge + Note */}
          <div className="flex items-end gap-2">
            <div className="w-[88px] shrink-0">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-gray)] mb-1.5 block">
                Nudge
              </span>
              <div className="relative">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={3650}
                  value={nudge}
                  onChange={(e) => setNudge(e.target.value)}
                  className="h-9 text-[12.5px] rounded-[10px] border-0 bg-[var(--color-black-2)]/40 shadow-none ring-0 focus-visible:ring-2 focus-visible:ring-[var(--color-success)]/15 pr-7 font-medium"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10.5px] font-medium text-[var(--color-text-gray)] pointer-events-none">
                  d
                </span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-gray)] mb-1.5 block">
                Note
              </span>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional context…"
                className="h-9 text-[12.5px] rounded-[10px] border-0 bg-[var(--color-black-2)]/40 shadow-none ring-0 focus-visible:ring-2 focus-visible:ring-[var(--color-success)]/15"
              />
            </div>
          </div>

          <p className="text-[10px] text-[var(--color-text-gray)] leading-relaxed -mt-1">
            Timer starts when the record reaches{" "}
            <span className="font-medium">From</span> (or any later status). It
            never blocks moves.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-3 py-2.5 border-t border-[var(--color-black-1)]/60 bg-[var(--color-black-2)]/20">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="h-9 px-3 text-[12.5px] font-medium text-[var(--color-text-gray)] hover:text-[var(--color-text-primary)] rounded-[10px] transition-colors"
          >
            Cancel
          </button>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={!canSubmit}
            title={
              canSubmit
                ? "Save milestone"
                : "Pick a target stage and status to enable"
            }
            className={cn(
              "h-9 px-4 text-[12.5px] font-medium rounded-[10px] gap-1.5 border-0 shadow-none transition-all",
              canSubmit
                ? "!bg-[var(--color-success)] !text-white hover:!bg-[#1a6b4c]"
                : "!bg-[var(--color-black-2)]/60 !text-[var(--color-text-gray)] cursor-not-allowed"
            )}
          >
            <Flag className="size-3.5" strokeWidth={2.5} />
            Set milestone
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
